import { EMPTY_SEGMENT } from '@church/domain'
import { useCreateList, useLists } from '@church/data'
import { faPlus, faUsers } from '@church/ui-icons'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { RequireAccess } from '../components/RequireAccess.tsx'
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorNote,
  Field,
  Icon,
  Input,
  listRow,
  PageHeader,
  QueryState,
  SidePanel,
} from '../components/ui.tsx'
import { PageAction } from '../components/PageAction.tsx'
import { pressableRow } from '../components/row-press.ts'
import { DisplayOptions } from '../components/DisplayOptions.tsx'
import { useListDisplay } from '../lib/display.ts'

/**
 * Saved lists. The count on each row is a cache — `refreshedAt` says when it
 * was taken — because rendering this screen must not run one segment query per
 * list. Opening a list recomputes for real; refreshing the saved count and
 * deleting the list live in its own page's options panel.
 */
export const Route = createFileRoute('/_authed/_shell/lists/')({
  component: ListsScreen,
})

/** What a list's row shows on a phone; everything shows from `sm` up. */
const ROW_FIELDS = [
  { id: 'rules', label: 'Its rules', default: true },
  { id: 'counted', label: 'When it was counted', default: false },
] as const

function ListsScreen() {
  const lists = useLists()
  const [adding, setAdding] = useState(false)
  const display = useListDisplay('lists', ROW_FIELDS)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Lists"
        action={
          <div className="flex items-center gap-1">
            <DisplayOptions fields={ROW_FIELDS} shown={display.shown} onChange={display.set} />
            <RequireAccess module="people" level="editor">
              <PageAction icon={faPlus} label="New list" onClick={() => setAdding(true)} />
            </RequireAccess>
          </div>
        }
      />

      <NewListPanel open={adding} onClose={() => setAdding(false)} />

      <QueryState
        query={lists}
        isEmpty={(rows) => rows.length === 0}
        safe="Nothing was changed: your lists are safe on the server."
        empty={
          <EmptyState
            title="No saved lists"
            hint="A list is a set of rules — “members at Main campus without an email” — that stays current."
            action={
              <RequireAccess module="people" level="editor">
                <Button icon={faPlus} onClick={() => setAdding(true)}>
                  New list
                </Button>
              </RequireAccess>
            }
          />
        }
      >
        {(rows) => (
          <Card bleed>
            <ul className="divide-y divide-border-subtle">
              {rows.map((list) => (
                <li key={list.id}>
                  <Link
                    to="/lists/$id"
                    params={{ id: list.id }}
                    className={`${listRow} ${pressableRow} flex items-center gap-3 no-underline`}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-base font-medium text-content-default">
                        {list.name}
                      </span>
                      <span
                        className={`block truncate text-sm text-content-muted ${display.hide('rules')}`}
                      >
                        {list.summary}
                      </span>
                      <span
                        className={`block text-xs text-content-subtle ${display.hide('counted')}`}
                      >
                        {list.refreshedAt
                          ? `Counted ${new Date(list.refreshedAt).toLocaleDateString()}`
                          : 'Never counted'}
                      </span>
                    </span>
                    <Chip size="md">
                      <Icon icon={faUsers} className="size-3" />
                      {list.memberCount ?? '—'}
                    </Chip>
                  </Link>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </QueryState>
    </div>
  )
}

function NewListPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateList()
  const navigate = useNavigate()
  const [name, setName] = useState('')
  // A fresh form each time it opens (it stays mounted, so it can slide out).
  const [openedFor, setOpenedFor] = useState(open)
  if (open !== openedFor) {
    setOpenedFor(open)
    if (open) setName('')
  }
  const close = () => {
    create.reset()
    onClose()
  }

  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => {
        if (!next) close()
      }}
      title="New list"
      description="Starts as “everyone” — add rules on the next screen."
      primary={
        <Button
          icon={faPlus}
          className="py-1.5"
          pending={create.isPending}
          pendingLabel="Creating…"
          disabled={name.trim() === ''}
          onClick={() =>
            create.mutate(
              { name: name.trim(), rules: EMPTY_SEGMENT },
              {
                onSuccess: (list) => {
                  close()
                  void navigate({ to: '/lists/$id', params: { id: list.id } })
                },
              },
            )
          }
        >
          Create
        </Button>
      }
    >
      <Field label="Name">
        <Input
          placeholder="Members without an email"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
      <ErrorNote error={create.error} />
    </SidePanel>
  )
}
